import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertTriangle, Users } from 'lucide-react';
import { useBillingContext } from '@/contexts/BillingContext';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/Card';

const ADMIN_ROLES = new Set(['admin', 'superadmin']);

const getReasonMessage = ({ trialExpired, quotaDepleted }: { trialExpired: boolean; quotaDepleted: boolean; }) => {
  if (trialExpired && quotaDepleted) {
    return 'Your free trial has ended and the quote allowance is exhausted.';
  }
  if (trialExpired) {
    return 'Your free trial has ended.';
  }
  if (quotaDepleted) {
    return 'Your company has used all available quotes for this period.';
  }
  return 'Your company must choose a subscription plan to continue.';
};

export const BillingAccessGate: React.FC = () => {
  const { billingStatus, quota, loading } = useBillingContext();
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const hasCompany = Boolean(user?.companyId || user?.orgId);
  const billingLoaded = !!billingStatus;
  const isAdmin = Boolean(user?.roles?.some(role => ADMIN_ROLES.has(role)));
  const isOnBillingPage = location.pathname.startsWith('/org/billing');

  const { trialExpired, quotaDepleted, shouldGate } = useMemo(() => {
    const trialInfo = billingStatus?.trial_info;
    const hasSubscription = billingStatus?.has_subscription;

    const trialExpiredFlag = Boolean(
      trialInfo && (
        trialInfo.expired === true ||
        (!trialInfo.active && !hasSubscription)
      )
    );

    const quotaDepletedFlag = Boolean(
      quota && !quota.unlimited && quota.remaining <= 0
    );

    const gate = Boolean(
      !loading && billingStatus && (trialExpiredFlag || quotaDepletedFlag)
    );

    return {
      trialExpired: trialExpiredFlag,
      quotaDepleted: quotaDepletedFlag,
      shouldGate: gate
    };
  }, [billingStatus, quota, loading]);

  if (!user || !hasCompany) {
    return null;
  }

  if (!billingLoaded || !shouldGate) {
    return null;
  }

  if (isAdmin && isOnBillingPage) {
    return null;
  }

  const reasonMessage = getReasonMessage({ trialExpired, quotaDepleted });

  const handleChoosePlan = () => {
    if (!isOnBillingPage) {
      navigate('/org/billing?tab=plans', {
        replace: false,
        state: { focusTab: 'plans' }
      });
    }
  };

  const handleSignOut = async () => {
    try {
      await logout?.();
    } catch (error) {
      console.error('Failed to log out after billing gate prompt', error);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[2000] flex items-center justify-center bg-background/95 backdrop-blur"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="billing-gate-title"
      aria-describedby="billing-gate-description"
    >
      <div className="w-full max-w-xl px-4">
        <Card className="shadow-xl">
          <CardHeader className="flex flex-col gap-2">
            <div className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" aria-hidden="true" />
              <span className="text-xs font-semibold uppercase tracking-wide">Action required</span>
            </div>
            <CardTitle id="billing-gate-title" className="text-2xl">
              {isAdmin ? 'Choose a plan to continue' : 'Billing action required'}
            </CardTitle>
            <p id="billing-gate-description" className="text-sm text-muted-foreground">
              {reasonMessage}
            </p>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {isAdmin ? (
              <>
                <p className="text-muted-foreground">
                  To regain access for everyone on your team, pick a subscription plan now. Stripe will update your existing subscription automatically and prorate any changes.
                </p>
                {!isOnBillingPage && (
                  <p className="text-muted-foreground">
                    You&apos;ll be taken to the Billing page where you can review plans and confirm the upgrade.
                  </p>
                )}
              </>
            ) : (
              <div className="flex items-start gap-2 rounded-md border border-border/60 bg-muted/40 p-3 text-muted-foreground">
                <Users className="mt-0.5 h-4 w-4" aria-hidden="true" />
                <div>
                  <p className="font-medium text-foreground">Please contact your billing admin.</p>
                  <p>
                    A plan needs to be selected before you can continue using the app. Share this message with your administrator so they can choose a plan in Billing.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
          <CardFooter className="flex flex-col gap-3 sm:flex-row sm:justify-end">
            {isAdmin ? (
              <Button onClick={handleChoosePlan} className="w-full sm:w-auto">
                Review plans
              </Button>
            ) : (
              <>
                <Button onClick={handleSignOut} variant="outline" className="w-full sm:w-auto">
                  Sign out
                </Button>
              </>
            )}
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default BillingAccessGate;
